import apiClient, { getApiErrorMessage } from './apiClient';
import { getAuthToken } from '../utils/storage';

const unwrapApiList = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  if (Array.isArray(data?.Items)) {
    return data.Items;
  }

  return [];
};

const normalizeVoucherValidation = (data) => ({
  canApply: Boolean(data?.CanApply ?? data?.canApply),
  reason: data?.Reason ?? data?.reason ?? null,
  originalAmount: data?.OriginalAmount ?? data?.originalAmount ?? 0,
  discountAmount: data?.DiscountAmount ?? data?.discountAmount ?? 0,
  payableAmount: data?.PayableAmount ?? data?.payableAmount ?? 0,
  voucherCode: data?.VoucherCode ?? data?.voucherCode ?? '',
  discountType: data?.DiscountType ?? data?.discountType ?? ''
});

const normalizeTransaction = (transaction) => ({
  id: transaction?.Id || transaction?.id || '',
  transactionType: transaction?.TransactionType || transaction?.transactionType || '',
  transactionTypeDescription:
    transaction?.TransactionTypeDescription || transaction?.transactionTypeDescription || '',
  amount: transaction?.Amount ?? transaction?.amount ?? 0,
  description: transaction?.Description || transaction?.description || '',
  ownerType: transaction?.OwnerType || transaction?.ownerType || '',
  ownerId: transaction?.OwnerId || transaction?.ownerId || '',
  createdAt: transaction?.CreatedAt || transaction?.createdAt || null
});

const normalizePayment = (payment) => ({
  id: payment?.Id || payment?.id || '',
  title: payment?.Title || payment?.title || '',
  description: payment?.Description || payment?.description || '',
  originalAmount: payment?.OriginalAmount ?? payment?.originalAmount ?? 0,
  payableAmount: payment?.PayableAmount ?? payment?.payableAmount ?? 0,
  discountAmount: payment?.DiscountAmount ?? payment?.discountAmount ?? 0,
  voucherCode: payment?.VoucherCode ?? payment?.voucherCode ?? null,
  provider: payment?.Provider || payment?.provider || '',
  status: payment?.Status || payment?.status || '',
  paidAt: payment?.PaidAt || payment?.paidAt || null,
  createdAt: payment?.CreatedAt || payment?.createdAt || null
});

const normalizeChargeResult = (data) => ({
  invoiceId: data?.InvoiceId || data?.invoiceId || '',
  paymentUrl: data?.PaymentUrl || data?.paymentUrl || '',
  authority: data?.Authority || data?.authority || '',
  provider: data?.Provider || data?.provider || '',
  originalAmount: data?.OriginalAmount ?? data?.originalAmount ?? 0,
  payableAmount: data?.PayableAmount ?? data?.payableAmount ?? 0,
  discountAmount: data?.DiscountAmount ?? data?.discountAmount ?? 0,
  voucherCode: data?.VoucherCode ?? data?.voucherCode ?? null
});

const ensureAuthenticated = () => {
  if (!getAuthToken()) {
    throw new Error('Login is required to access wallet.');
  }
};

export const getWalletBalanceApi = async () => {
  ensureAuthenticated();

  try {
    const { data } = await apiClient.get('/Wallet/balance');
    return {
      balance: data?.Balance ?? data?.balance ?? 0
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Wallet balance request failed.'));
  }
};

export const getWalletPaymentsApi = async () => {
  ensureAuthenticated();

  try {
    const { data } = await apiClient.get('/Wallet/payments');
    return unwrapApiList(data).map(normalizePayment);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Wallet payments request failed.'));
  }
};

export const getWalletTransactionsApi = async () => {
  ensureAuthenticated();

  try {
    const { data } = await apiClient.get('/Wallet/transactions');
    return unwrapApiList(data).map(normalizeTransaction);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Wallet transactions request failed.'));
  }
};

export const validateWalletVoucherApi = async ({ code, amount }) => {
  ensureAuthenticated();

  try {
    const { data } = await apiClient.post('/Wallet/vouchers/validate', {
      Code: code,
      Amount: amount
    });

    return normalizeVoucherValidation(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Voucher validation failed.'));
  }
};

export const chargeWalletApi = async ({ amount, voucherCode }) => {
  ensureAuthenticated();

  const payload = {
    Amount: amount,
    Provider: 'Zibal'
  };

  if (voucherCode) {
    payload.VoucherCode = voucherCode;
  }

  try {
    const { data } = await apiClient.post('/Wallet/charge', payload);
    return normalizeChargeResult(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Wallet charge request failed.'));
  }
};
